import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../../services/api';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  ArchiveBoxIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const ContactMessageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [adminNotes, setAdminNotes] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Fetch contact message details
  const { data, isLoading, isError, error } = useQuery(
    ['contactMessage', id],
    async () => {
      try {
        console.log('Fetching contact message details for ID:', id);
        console.log('Auth token:', localStorage.getItem('token'));
        const response = await api.get(`/api/v1/admin/contact-messages/${id}`);
        console.log('Response:', response.data);
        setAdminNotes(response.data.data.admin_notes || '');
        return response.data;
      } catch (err) {
        console.error('Error fetching contact message:', err);
        throw err;
      }
    }
  );

  // Update message mutation
  const updateMutation = useMutation(
    async (updateData) => {
      const response = await api.put(
        `/api/v1/admin/contact-messages/${id}`,
        updateData
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['contactMessage', id]);
        setIsEditing(false);
      }
    }
  );

  // Mark as replied mutation
  const markAsRepliedMutation = useMutation(
    async () => {
      const response = await api.put(
        `/api/v1/admin/contact-messages/${id}/mark-as-replied`,
        {}
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['contactMessage', id]);
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async () => {
      const response = await api.delete(
        `/api/v1/admin/contact-messages/${id}`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        navigate('/admin/contact-messages');
      }
    }
  );

  const handleSaveNotes = () => {
    updateMutation.mutate({ admin_notes: adminNotes });
  };

  const handleStatusChange = (status) => {
    updateMutation.mutate({ status });
  };

  const handleMarkAsReplied = () => {
    markAsRepliedMutation.mutate();
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      deleteMutation.mutate();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <div className="text-red-500">Error: {error.message}</div>;
  }

  const message = data.data;

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Link to="/admin/contact-messages" className="mr-4 text-gray-500 hover:text-gray-700">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900">Message Details</h1>
        </div>
        <div className="flex space-x-2">
          {message.status !== 'replied' && (
            <button
              onClick={handleMarkAsReplied}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
              Mark as Replied
            </button>
          )}
          
          {message.status !== 'archived' && (
            <button
              onClick={() => handleStatusChange('archived')}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              <ArchiveBoxIcon className="h-4 w-4 mr-1" />
              Archive
            </button>
          )}
          
          <button
            onClick={handleDelete}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <TrashIcon className="h-4 w-4 mr-1" />
            Delete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Message Content */}
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <h2 className="text-xl font-medium text-gray-900 mb-4">{message.subject}</h2>
            <p className="text-gray-700 whitespace-pre-line">{message.message}</p>
          </div>

          {/* Admin Notes */}
          <div className="bg-yellow-50 p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Admin Notes</h3>
              {isEditing ? (
                <div className="space-x-2">
                  <button
                    onClick={handleSaveNotes}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    disabled={updateMutation.isLoading}
                  >
                    {updateMutation.isLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setAdminNotes(message.admin_notes || '');
                    }}
                    className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Edit Notes
                </button>
              )}
            </div>
            
            {isEditing ? (
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full h-32 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                placeholder="Add notes about this message..."
              />
            ) : (
              <div className="bg-white p-4 rounded border border-gray-200 min-h-[100px]">
                {message.admin_notes ? (
                  <p className="text-gray-700 whitespace-pre-line">{message.admin_notes}</p>
                ) : (
                  <p className="text-gray-400 italic">No admin notes yet.</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          {/* Sender Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Sender Information</h3>
            
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-100">
                    <span className="text-sm font-medium leading-none text-gray-800">
                      {message.name.charAt(0).toUpperCase()}
                    </span>
                  </span>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">{message.name}</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-500">
                    <a href={`mailto:${message.email}`} className="hover:underline">
                      {message.email}
                    </a>
                  </p>
                </div>
              </div>
              
              {message.phone && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-500">
                      <a href={`tel:${message.phone}`} className="hover:underline">
                        {message.phone}
                      </a>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Message Status */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Message Status</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Status:</span>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  message.status === 'new' ? 'bg-blue-100 text-blue-800' :
                  message.status === 'read' ? 'bg-green-100 text-green-800' :
                  message.status === 'replied' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {message.status.charAt(0).toUpperCase() + message.status.slice(1)}
                </span>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-gray-500">Received:</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(message.created_at)}</p>
                </div>
              </div>
              
              {message.read_at && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-500">Read:</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(message.read_at)}</p>
                  </div>
                </div>
              )}
              
              {message.replied_at && (
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-gray-500">Replied:</p>
                    <p className="text-sm font-medium text-gray-900">{formatDate(message.replied_at)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactMessageDetail;
